import React, { 
  createContext, 
  useContext, 
  useState, 
  useCallback, 
  ReactNode,
  useEffect,
  useRef,
  useMemo
} from 'react';
import {
  notificationApi,
  Notification,
  NotificationType,
  NotificationStatus,
} from '../services/notificationApi';
import { useAuth } from './AuthContext';
import { isNativeMobileApp } from '../utils/mobileApp';
import { isTenantAppNotification } from '../utils/tenantNotifications';

export interface NotificationContextType {
  // State
  notifications: Notification[];
  unreadNotifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  
  // Actions
  fetchNotifications: (limit?: number, offset?: number) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (notificationId: number) => Promise<void>;
  markMultipleAsRead: (notificationIds: number[]) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: number) => Promise<void>;
  getNotificationsByType: (type: NotificationType | string) => Notification[];
  
  // Specific notification fetchers
  getTrackingNotifications: () => Promise<Notification[]>;
  getMessageNotifications: () => Promise<Notification[]>;
  getPaymentNotifications: () => Promise<Notification[]>;
  getScheduleNotifications: () => Promise<Notification[]>;
  getPropertyNotifications: (propertyId: number) => Promise<Notification[]>;
  getContractNotifications: (contractId: number) => Promise<Notification[]>;
  
  // Polling
  startPolling: (interval?: number) => void;
  stopPolling: () => void;
  isPolling: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
  pollingInterval?: number; // Default: 30000ms (30 seconds)
}

const DEFAULT_POLLING_INTERVAL = 30000; // 30 seconds

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
  pollingInterval = DEFAULT_POLLING_INTERVAL,
}) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch all notifications
  const fetchNotifications = useCallback(async (limit: number = 50, offset: number = 0) => {
    try {
      setLoading(true);
      setError(null);
      const response = await notificationApi.getNotifications(limit, offset);
      const notificationsData = Array.isArray(response.notifications) ? response.notifications : [];
      const isTenant = user?.accountType === 'tenant';
      const visible = isTenant
        ? notificationsData.filter((item) => isTenantAppNotification(item))
        : notificationsData;
      setNotifications(visible);
      setUnreadCount(
        typeof response.unread_count === 'number'
          ? response.unread_count
          : visible.filter((item) => item.status === NotificationStatus.UNREAD).length,
      );
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to fetch notifications');
      console.error('Error fetching notifications:', err);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [user?.accountType]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await notificationApi.getUnreadCount();
      setUnreadCount(data.unread_count);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  }, []);

  // Mark as read
  const markAsRead = useCallback(async (notificationId: number) => {
    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId
            ? { ...notif, status: NotificationStatus.READ, read_at: new Date().toISOString() }
            : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }, []);

  // Mark multiple as read
  const markMultipleAsRead = useCallback(async (notificationIds: number[]) => {
    try {
      await notificationApi.markMultipleAsRead(notificationIds);
      setNotifications(prev =>
        prev.map(notif =>
          notificationIds.includes(notif.id)
            ? { ...notif, status: NotificationStatus.READ, read_at: new Date().toISOString() }
            : notif
        )
      );
      const unreadInBatch = notifications.filter(n => 
        notificationIds.includes(n.id) && n.status === NotificationStatus.UNREAD
      ).length;
      setUnreadCount(prev => Math.max(0, prev - unreadInBatch));
    } catch (err) {
      console.error('Error marking multiple notifications as read:', err);
    }
  }, [notifications]);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev =>
        prev.map(notif => ({
          ...notif,
          status: NotificationStatus.READ,
          read_at: new Date().toISOString()
        }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, []);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId: number) => {
    try {
      await notificationApi.deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));

      // Adjust unread count if deleted notification was unread
      const deletedNotif = notifications.find(n => n.id === notificationId);
      if (deletedNotif?.status === NotificationStatus.UNREAD) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  }, [notifications]);

  // Get notifications by type
  const getNotificationsByType = useCallback((type: NotificationType | string) => {
    return notifications.filter(n => n.notification_type === type);
  }, [notifications]);

  // Fetch specific notification types
  const getTrackingNotifications = useCallback(async () => {
    try {
      return await notificationApi.getTrackingNotifications();
    } catch (err) {
      console.error('Error fetching tracking notifications:', err);
      return [];
    }
  }, []);

  const getMessageNotifications = useCallback(async () => {
    try {
      return await notificationApi.getMessageNotifications();
    } catch (err) {
      console.error('Error fetching message notifications:', err);
      return [];
    }
  }, []);

  const getPaymentNotifications = useCallback(async () => {
    try {
      return await notificationApi.getPaymentNotifications();
    } catch (err) {
      console.error('Error fetching payment notifications:', err);
      return [];
    }
  }, []);

  const getScheduleNotifications = useCallback(async () => {
    try {
      return await notificationApi.getScheduleNotifications();
    } catch (err) {
      console.error('Error fetching schedule notifications:', err);
      return [];
    }
  }, []);

  const getPropertyNotifications = useCallback(async (propertyId: number) => {
    try {
      return await notificationApi.getPropertyNotifications(propertyId);
    } catch (err) {
      console.error('Error fetching property notifications:', err);
      return [];
    }
  }, []);

  const getContractNotifications = useCallback(async (contractId: number) => {
    try {
      return await notificationApi.getContractNotifications(contractId);
    } catch (err) {
      console.error('Error fetching contract notifications:', err);
      return [];
    }
  }, []);

  // Polling functions
  const startPolling = useCallback((interval: number = pollingInterval) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    setIsPolling(true);
    
    // Initial fetch
    fetchUnreadCount();
    
    // Set polling interval
    pollingIntervalRef.current = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      fetchUnreadCount();
    }, interval);
  }, [fetchUnreadCount]); // ✅ FIX: Chỉ depend on fetchUnreadCount, không depend trên pollingInterval

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  // Calculate unread notifications
  const unreadNotifications = useMemo(() => {
    if (!notifications || !Array.isArray(notifications)) {
      return [];
    }
    return notifications.filter(n => n.status === NotificationStatus.UNREAD);
  }, [notifications]);

  // Auto-start polling when user is authenticated
  // ✅ FIX: Chỉ depend on user.id, remove pollingInterval từ dependency
  useEffect(() => {
    if (user && user.id) {
      if (isNativeMobileApp()) {
        return;
      }
      if (!isPolling) {
        startPolling(pollingInterval);
      }
    } else {
      stopPolling();
    }

    return () => {
      if (!user?.id) {
        stopPolling();
      }
    };
  }, [user?.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  const value: NotificationContextType = {
    notifications,
    unreadNotifications,
    unreadCount,
    loading,
    error,
    isPolling,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markMultipleAsRead,
    markAllAsRead,
    deleteNotification,
    getNotificationsByType,
    getTrackingNotifications,
    getMessageNotifications,
    getPaymentNotifications,
    getScheduleNotifications,
    getPropertyNotifications,
    getContractNotifications,
    startPolling,
    stopPolling,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
